import React from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import {
    BuilderPartnersStrip, StatsStripV2,
} from "@/components/PageBlocks";
import {
    Eye, Target, ShieldCheck, HeartHandshake, Award, Sparkles, Users2, Linkedin,
    ArrowRight, Landmark, IndianRupee, HeadphonesIcon, TrendingUp, ScrollText,
    Search, CalendarCheck, Gift, Lightbulb,
} from "lucide-react";
const HERO_IMG   = "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=2400&q=80";
const OFFICE_IMG = "/about/who-we-are.png";

const ABOUT_STATS = [
    { v: "250+",       l: "Verified Projects" },
    { v: "10K+",       l: "Happy Customers"   },
    { v: "15+",        l: "Years of Trust"    },
    { v: "25+",        l: "Cities Served"     },
    { v: "750+",       l: "Channel Partners"  },
    { v: "₹8,500 Cr+", l: "Sales Value"       },
];

const HERO_PILLARS = [
    { icon: ShieldCheck,   l: "Legal First",     s: "Every property 100% verified" },
    { icon: Users2,        l: "Client First",    s: "Solutions tailored to your goals" },
    { icon: Target,        l: "Integrity First", s: "No hidden charges, no surprises" },
    { icon: TrendingUp,    l: "Growth First",    s: "Creating long-term value for all" },
];

const JOURNEY_STEPS = [
    { icon: Search,        n: "01", t: "Discover",      d: "Explore India's largest curated inventory of legally verified premium properties." },
    { icon: ShieldCheck,   n: "02", t: "Verify",        d: "Our 120-point legal & structural audit runs before you even see the listing." },
    { icon: Lightbulb,     n: "03", t: "Advise",        d: "A dedicated advisor helps you shortlist based on ROI, lifestyle & investment goals." },
    { icon: CalendarCheck, n: "04", t: "Visit & Decide",d: "Book site visits with virtual walkthroughs and on-ground EBG hospitality." },
    { icon: HeartHandshake,n: "05", t: "Secure",        d: "End-to-end handling of paperwork, home loan, registration and possession." },
    { icon: Gift,          n: "06", t: "Celebrate",     d: "After-sales concierge, property management & lifelong EBG membership benefits." },
];

const VALUES = [
    { icon: ShieldCheck,    t: "Trust",           d: "Verified properties, transparent dealings, on-time delivery." },
    { icon: HeartHandshake, t: "Customer Centric",d: "Every decision made with the buyer's peace of mind first." },
    { icon: Award,          t: "Excellence",      d: "World-class experiences across every touchpoint." },
    { icon: ScrollText,     t: "Integrity",       d: "We say what we do, and do what we say. Every single time." },
    { icon: Sparkles,       t: "Innovation",      d: "Technology, AI and legal expertise combined at scale." },
    { icon: Users2,         t: "Partnership",     d: "We grow with builders, partners and customers alike." },
];

const WHY_CHOOSE = [
    { icon: ShieldCheck,   t: "Legal Verification",     d: "120+ checkpoints on every property before it goes live." },
    { icon: IndianRupee,   t: "Zero Hidden Charges",    d: "Transparent pricing, no surprises at any stage." },
    { icon: Landmark,      t: "Direct from Builder",   d: "Official partnerships with India's leading developers." },
    { icon: Users2,        t: "Dedicated Experts",      d: "A named advisor and legal counsel for every purchase." },
    { icon: HeartHandshake,t: "End-to-End Support",     d: "From shortlist to registry, every step is handled." },
    { icon: HeadphonesIcon,t: "After-Sales Care",       d: "We stay connected long after possession." },
];

const TEAM = [
    { name: "Rohit Sharma",    role: "Founder & CEO",          bio: "20+ years across property advisory, private equity and family-office real estate.",   img: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=800&q=80" },
    { name: "Anjali Mehra",    role: "Chief Legal Officer",    bio: "Former Bombay HC counsel. Heads our 120-point legal verification framework.",         img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&q=80" },
    { name: "Vikram Sinha",    role: "Head of Investments",    bio: "Ex-JLL Research. Publishes the EBG Market Intelligence Report every quarter.",         img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80" },
    { name: "Arun Malhotra",   role: "Head of Channel Partners", bio: "Building India's largest verified partner ecosystem, 750+ agents strong.",           img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=800&q=80" },
    { name: "Neha Kapoor",     role: "Head of NRI Desk",       bio: "Handles NRI & UAE clients end-to-end — FEMA, remittance, POA, possession.",           img: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=800&q=80" },
];

export default function About() {
    return (
        <PageShell navVariant="dark">
            {/* ================= HERO ================= */}
            <section data-testid="about-hero" className="relative bg-[#0a0d18] text-white overflow-hidden">
                <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50"/>
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/70 to-transparent"/>
                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-36 pb-20 md:pt-44 md:pb-24">
                    <div className="text-[10px] tracking-[0.28em] font-bold text-[var(--ebg-gold)] uppercase mb-5">About EBG Realty</div>
                    <h1 className="font-serif text-4xl md:text-6xl lg:text-[80px] leading-[1.02] max-w-4xl">
                        Built on <em className="not-italic text-[var(--ebg-gold)]">Trust.</em><br/>
                        Driven by <em className="not-italic text-[var(--ebg-gold)]">Purpose.</em>
                    </h1>
                    <p className="text-white/70 max-w-2xl mt-6 leading-relaxed text-base md:text-lg">
                        EBG Realty is India&apos;s most trusted legal-verified real estate platform, committed to
                        transparency, security, and delivering the best real estate experiences.
                    </p>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6">
                        {HERO_PILLARS.map((p, i) => (
                            <div key={i} data-testid={`about-pillar-${i}`} className="text-center md:text-left">
                                <span className="w-14 h-14 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 text-[var(--ebg-gold)] flex items-center justify-center mx-auto md:mx-0">
                                    <p.icon size={20} strokeWidth={1.8}/>
                                </span>
                                <div className="text-[11px] uppercase tracking-[0.24em] font-bold text-white mt-4">{p.l}</div>
                                <div className="text-[11px] text-white/55 mt-1 max-w-[180px] mx-auto md:mx-0 leading-snug">{p.s}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= WHO WE ARE ================= */}
            <section data-testid="who-we-are" className="bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div className="lg:col-span-7">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Who We Are</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.02]">
                            Redefining Real Estate with<br/>
                            <em className="not-italic text-[var(--ebg-gold)]">Trust &amp; Technology.</em>
                        </h2>
                        <p className="text-black/65 leading-relaxed mt-6 max-w-lg">
                            EBG Realty was founded on a single, uncompromising principle — every buyer deserves a
                            legally verified, transparent, and delightful home-buying experience. Fifteen years
                            later, we are still obsessed with that principle.
                        </p>
                        <p className="text-black/65 leading-relaxed mt-4 max-w-lg">
                            Our teams across India and Dubai combine legal expertise, market intelligence and
                            technology to make every property decision safer, sharper and smarter.
                        </p>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <img src={OFFICE_IMG} alt="EBG Realty Office" className="w-full aspect-[4/5] object-cover border border-black/10"/>
                        <div className="absolute -bottom-5 -left-5 bg-[var(--ebg-gold)] text-black px-6 py-4 hidden md:block">
                            <div className="display-num text-3xl leading-none tabular-nums">15+</div>
                            <div className="text-[10px] uppercase tracking-[0.24em] font-bold mt-1">Years of Trust</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= STATS ================= */}
            <StatsStripV2 items={ABOUT_STATS}/>

            {/* ================= MISSION / VISION ================= */}
            <section data-testid="mission-vision" className="bg-[#0a0d18] text-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-2 gap-6 md:gap-8">
                    {[
                        {
                            icon: Eye,
                            eyebrow: "Our Vision",
                            title: "To Be India's Most Trusted Real Estate Platform.",
                            body: "We envision a world where every property transaction in India is legally verified, transparent and delightful — where trust replaces anxiety at every step of the journey.",
                        },
                        {
                            icon: Target,
                            eyebrow: "Our Mission",
                            title: "Deliver Confidence in Every Deal.",
                            body: "Combine legal expertise, market intelligence and technology to help every buyer, investor and channel partner move forward with clarity and confidence.",
                        },
                    ].map((b, i) => (
                        <div key={i} data-testid={`mv-block-${i}`} className="border border-white/10 bg-white/[0.03] p-8 md:p-10 hover:border-[var(--ebg-gold-line)] transition-colors">
                            <span className="w-14 h-14 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center">
                                <b.icon size={22}/>
                            </span>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)] mt-6">{b.eyebrow}</div>
                            <h3 className="font-serif text-2xl md:text-3xl mt-3 leading-tight">{b.title}</h3>
                            <p className="text-white/65 mt-5 leading-relaxed">{b.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ================= VALUES ================= */}
            <section data-testid="about-values" className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Our Values</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">The Principles That<br/>Guide Us.</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {VALUES.map((v, i) => (
                            <div key={i} data-testid={`value-${i}`} className="bg-white border border-black/8 p-7 hover:border-[var(--ebg-gold-line)] hover:shadow-[0_20px_40px_-25px_rgba(0,0,0,0.25)] transition-all group">
                                <span className="w-12 h-12 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center">
                                    <v.icon size={18} strokeWidth={1.8}/>
                                </span>
                                <h3 className="font-serif text-xl md:text-2xl mt-5">{v.t}</h3>
                                <p className="text-black/60 mt-3 text-[14px] leading-relaxed">{v.d}</p>
                                <div className="w-6 h-px bg-[var(--ebg-gold)] mt-5 group-hover:w-12 transition-all"/>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= WHY CHOOSE US ================= */}
            <section data-testid="why-choose" className="bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
                        <div className="lg:col-span-6">
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Why Choose Us</div>
                            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">More Than a Broker.<br/><em className="not-italic text-[var(--ebg-gold)]">We&apos;re Your Growth Partner.</em></h2>
                        </div>
                        <div className="lg:col-span-6">
                            <p className="text-black/60 max-w-lg leading-relaxed">Six operational commitments that turn every property purchase from a transaction into a lifetime relationship.</p>
                            <Link
                                to="/services"
                                data-testid="why-explore-services"
                                className="mt-6 inline-flex items-center gap-2 bg-black text-white px-8 py-4 text-[11px] uppercase tracking-[0.28em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors"
                            >
                                Explore Our Services <ArrowRight size={12}/>
                            </Link>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {WHY_CHOOSE.map((w, i) => (
                            <div key={i} data-testid={`why-${i}`} className="border border-black/8 bg-[var(--ebg-ivory)] p-6 hover:border-black transition-colors">
                                <span className="w-11 h-11 rounded-full bg-[var(--ebg-gold)] text-black flex items-center justify-center">
                                    <w.icon size={17} strokeWidth={1.8}/>
                                </span>
                                <div className="font-serif text-lg md:text-xl mt-4">{w.t}</div>
                                <p className="text-black/60 text-[13px] mt-2 leading-relaxed">{w.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= YOUR JOURNEY ================= */}
            <section data-testid="your-journey" className="bg-white">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Your Journey, Our Commitment</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">A seamless journey<br/>from start to finish.</h2>
                        <p className="text-black/60 mt-5 leading-relaxed">Six carefully designed steps — every one of them handled by a named EBG expert.</p>
                    </div>

                    <div className="relative">
                        {/* dashed connector line */}
                        <div className="hidden lg:block absolute top-8 left-[8%] right-[8%] border-t-2 border-dashed border-[var(--ebg-gold)]/30 z-0"/>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-8 relative z-10">
                            {JOURNEY_STEPS.map((s, i) => (
                                <div key={i} data-testid={`journey-step-${i}`} className="text-center">
                                    <div className="w-16 h-16 rounded-full bg-white border-2 border-[var(--ebg-gold)] text-[var(--ebg-gold)] flex items-center justify-center mx-auto shadow-[0_8px_24px_-12px_rgba(0,0,0,0.2)]">
                                        <s.icon size={22} strokeWidth={1.8}/>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-black/40 mt-4">Step {s.n}</div>
                                    <div className="font-serif text-xl mt-1">{s.t}</div>
                                    <p className="text-black/60 text-[13px] mt-2.5 leading-relaxed max-w-[200px] mx-auto">{s.d}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ================= LEADERSHIP TEAM ================= */}
            <section data-testid="leadership-team" className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24">
                    <div className="text-center max-w-2xl mx-auto mb-14">
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">Our Leadership Team</div>
                        <h2 className="font-serif text-4xl md:text-5xl mt-3 leading-[1.05]">People behind the platform.</h2>
                    </div>
                    <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                        {TEAM.map((m, i) => (
                            <article key={m.name} data-testid={`team-${i}`} className="group bg-white border border-black/8 hover:shadow-[0_20px_40px_-25px_rgba(0,0,0,0.25)] transition-all">
                                <div className="aspect-[4/5] overflow-hidden bg-black/5 relative">
                                    <img src={m.img} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                                    <a href="#" onClick={(e) => e.preventDefault()} className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-white/95 flex items-center justify-center text-[#0a66c2] hover:bg-[#0a66c2] hover:text-white transition-colors" aria-label={`${m.name} on LinkedIn`}>
                                        <Linkedin size={15}/>
                                    </a>
                                </div>
                                <div className="p-5">
                                    <div className="font-serif text-lg leading-tight">{m.name}</div>
                                    <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--ebg-gold)] mt-2">{m.role}</div>
                                    <p className="text-black/55 text-[13px] mt-3 leading-relaxed">{m.bio}</p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* ================= BUILDER PARTNERS ================= */}
            <BuilderPartnersStrip eyebrow="Trusted By" heading="India's leading developers."/>
        </PageShell>
    );
}
